import { DropdownIndicator } from "components/components/Forms/DropdownIndicator/DropdownIndicator";
import { RequestStatus } from "hooks/useRequest";
import React from "react";
import { useEffect, useMemo, useState } from "react";
import Select, { InputActionMeta, Theme } from "react-select";

import {
  convertToOption,
  FilterOption,
  fuzzyFilter,
  mapToOptions,
} from "./helpers";
import {
  augmentTheme,
  dropdownStyles,
  InputContainer,
  BoldedLabel,
  AutoInputContainer,
  MiniInputContainer,
} from "./style";

type InputSize = "mini" | "auto";

interface Props {
  values: string[];
  selectedValue: string | null;
  onChange: (value: string) => void;
  ariaLabel: string;
  label?: string;
  status?: RequestStatus;
  placeholder?: string;
  isDisabled?: boolean;
  size?: InputSize;
}

const Dropdown: React.FC<Props> = ({
  values,
  selectedValue,
  onChange,
  ariaLabel,
  label,
  status = RequestStatus.SUCCESSFUL_OR_NOT_STARTED,
  placeholder = "Select or search",
  isDisabled = false,
  size,
}) => {
  const allOptions = useMemo(() => mapToOptions(values), [values]);
  const selectedOption = useMemo(() => {
    if (selectedValue === null) {
      return null;
    }
    return convertToOption(selectedValue);
  }, [selectedValue]);

  const [filterString, setFilterString] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<FilterOption[]>([]);

  useEffect(
    () => setFilteredOptions(fuzzyFilter(allOptions, filterString)),
    [allOptions, filterString]
  );

  const onInputChange = (input: string, { action }: InputActionMeta): void => {
    if (action === "input-change") {
      // By clearing the filteredOptions array when user input is changed,
      // react-select defaults to focusing on the first option in the dropdown list
      // instead of remembering which option was previously focused.
      setFilteredOptions([]);
      // only set the input when the action that caused the
      // change equals to "input-change" and ignore the other
      // ones like: "set-value", "input-blur", and "menu-close"
      setFilterString(input);
    }
  };

  const onBlur = (): void => {
    setFilterString("");
    setFilteredOptions(allOptions);
  };

  const onSelectionChange = (newOption: unknown): void =>
    onChange((newOption as FilterOption)?.value ?? null);

  const containerMap: Record<string, React.ElementType> = {
    mini: MiniInputContainer,
    auto: AutoInputContainer,
  };

  const Container: React.ElementType = size
    ? containerMap[size]
    : InputContainer;

  return (
    <Container>
      {label && <BoldedLabel htmlFor={ariaLabel}>{label}</BoldedLabel>}
      <Select
        options={filteredOptions}
        aria-label={ariaLabel}
        filterOption={null}
        value={selectedOption}
        onInputChange={onInputChange}
        onChange={onSelectionChange}
        onBlur={onBlur}
        openMenuOnFocus={true}
        components={{
          DropdownIndicator,
          IndicatorSeparator: null,
          ClearIndicator: undefined,
        }}
        isMulti={false}
        isClearable={true}
        placeholder={placeholder}
        theme={(theme): Theme => augmentTheme(theme, status)}
        isLoading={status === RequestStatus.IN_PROGRESS}
        isDisabled={isDisabled}
        styles={dropdownStyles}
      />
    </Container>
  );
};

export default Dropdown;
